"use client";

import {
  ChevronRight,
  Sparkles,
  ArrowLeft,
  FileText,
  Calendar,
  Mail,
} from "lucide-react";
import { useState } from "react";

import { Link } from "@/shared/ui/design-system/link.js";

// Header Component (reused from landing)
const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <header className="fixed top-0 right-0 left-0 z-50 border-b border-gray-800/50 bg-black/70 backdrop-blur-md">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <nav className="flex h-14 items-center justify-between">
          <Link href="/" className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-white">
              <Sparkles className="h-4 w-4 text-black" />
            </div>
            <span className="text-lg font-semibold text-white">NovioAI</span>
          </Link>

          <div className="hidden items-center gap-8 md:flex">
            <Link
              href="/#features"
              className="text-sm text-gray-400 transition-colors hover:text-white"
            >
              Возможности
            </Link>
            <Link
              href="/#pricing"
              className="text-sm text-gray-400 transition-colors hover:text-white"
            >
              Тарифы
            </Link>
            <button
              type="button"
              className="rounded-lg bg-white px-4 py-1.5 text-sm font-medium text-black transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
              disabled
            >
              Скоро
            </button>
            {/* <button type="button" className="bg-white text-black px-4 py-1.5 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors">
              Попробовать
            </button> */}
          </div>

          <button
            type="button"
            aria-label="Open navigation menu"
            className="text-gray-400 transition-colors hover:text-white md:hidden"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        </nav>

        {isMenuOpen && (
          <div className="border-t border-gray-800/50 py-4 md:hidden">
            <div className="flex flex-col gap-3">
              <Link
                href="/#features"
                className="px-4 py-2 text-sm text-gray-400 transition-colors hover:text-white"
              >
                Функции
              </Link>
              <Link
                href="/#pricing"
                className="px-4 py-2 text-sm text-gray-400 transition-colors hover:text-white"
              >
                Цены
              </Link>
              <button
                type="button"
                className="mx-4 mt-2 rounded-lg bg-white px-4 py-2 text-sm font-medium text-black"
              >
                Попробовать
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

// Table of Contents Component
const TableOfContents = ({
  sections,
  activeSection,
}: {
  sections: { id: string; title: string }[];
  activeSection: string;
}) => (
  <div className="sticky top-20 rounded-xl border border-gray-800/50 bg-gray-900/50 p-6 backdrop-blur-sm">
    <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold text-white">
      <FileText className="h-5 w-5" />
      Содержание
    </h3>
    <ul className="space-y-2">
      {sections.map((section) => (
        <li key={section.id}>
          <Link
            href={`#${section.id}`}
            className={`flex items-center gap-2 py-1 text-sm transition-colors hover:text-white ${
              activeSection === section.id ? "text-white" : "text-gray-400"
            }`}
          >
            <ChevronRight className="h-3 w-3" />
            {section.title}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

// Section Component
const Section = ({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) => (
  <section id={id} className="mb-12 scroll-mt-24">
    <h2 className="mb-6 border-b border-gray-800 pb-2 text-2xl font-semibold text-white">
      {title}
    </h2>
    <div className="space-y-4 leading-relaxed text-gray-300">{children}</div>
  </section>
);

const OfferPage = () => {
  const activeSection = "introduction";

  const sections = [
    { id: "introduction", title: "1. Вводное положение" },
    { id: "definitions", title: "2. Определения" },
    { id: "services", title: "3. Описание Услуг" },
    { id: "accounts", title: "4. Учетные записи" },
    { id: "usage-rules", title: "5. Правила использования" },
    { id: "content", title: "6. Контент и данные пользователя" },
    { id: "payment", title: "7. Оплата, подписки и возврат средств" },
    { id: "ip", title: "8. Интеллектуальная собственность" },
    { id: "termination", title: "9. Прекращение действия договора" },
    {
      id: "disclaimers",
      title: "10. Отказ от гарантий и ограничение ответственности",
    },
    { id: "disputes", title: "11. Разрешение споров" },
    { id: "changes", title: "12. Изменение условий" },
    { id: "requisites", title: "13. Реквизиты Компании" },
    { id: "contact", title: "14. Контактная информация" },
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-gray-950 to-black pt-20 pb-16">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <Link
              href="/"
              className="mb-6 inline-flex items-center gap-2 text-gray-400 transition-colors hover:text-white"
            >
              <ArrowLeft className="h-4 w-4" />
              Вернуться на главную
            </Link>

            <h1 className="mb-4 bg-gradient-to-br from-white to-gray-400 bg-clip-text text-4xl font-semibold tracking-tight text-transparent md:text-5xl">
              Публичная оферта
            </h1>
            <p className="mb-8 text-xl text-gray-400">
              на использование программного продукта NovioAI Assistant
            </p>

            <div className="flex flex-col items-center justify-center gap-4 text-sm text-gray-500 sm:flex-row">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>Дата вступления в силу: 03.07.2025</span>
              </div>
              <div className="hidden h-1 w-1 rounded-full bg-gray-600 sm:block" />
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                <span>Последнее обновление: 03.07.2025</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-4">
          {/* Table of Contents */}
          <div className="lg:col-span-1">
            <TableOfContents
              sections={sections}
              activeSection={activeSection}
            />
          </div>

          {/* Content */}
          <div className="lg:col-span-3">
            <div className="rounded-xl border border-gray-800/50 bg-gray-900/30 p-8 backdrop-blur-sm">
              <Section id="introduction" title="1. Вводное положение">
                <p>
                  Настоящий документ является официальным предложением
                  (публичной офертой) ИП Стрельников Станислав Дмитриевич
                  заключить договор на условиях, изложенных ниже.
                </p>
                <p>
                  Используя программный продукт NovioAI Assistant (далее —
                  «Сервис»), вы (далее — «Пользователь») подтверждаете, что
                  внимательно ознакомились с условиями настоящей Оферты и
                  принимаете их в полном объеме без каких-либо изъятий и/или
                  ограничений. Если вы не согласны с условиями настоящей Оферты,
                  пожалуйста, прекратите использование Сервиса.
                </p>
              </Section>

              <Section id="definitions" title="2. Определения">
                <div className="space-y-4">
                  <div className="rounded-lg bg-gray-800/30 p-4">
                    <p>
                      <strong className="text-white">Сервис</strong> —
                      программное обеспечение NovioAI Assistant для ПК (macOS и
                      Windows), предоставляющее функции интеллектуального
                      ассистента.
                    </p>
                  </div>
                  <div className="rounded-lg bg-gray-800/30 p-4">
                    <p>
                      <strong className="text-white">Пользователь</strong> —
                      физическое или юридическое лицо, зарегистрировавшееся в
                      Сервисе и/или использующее его функционал.
                    </p>
                  </div>
                  <div className="rounded-lg bg-gray-800/30 p-4">
                    <p>
                      <strong className="text-white">Учетная запись</strong> —
                      уникальный профиль Пользователя, созданный при регистрации
                      в Сервисе.
                    </p>
                  </div>
                  <div className="rounded-lg bg-gray-800/30 p-4">
                    <p>
                      <strong className="text-white">
                        Контент пользователя
                      </strong>{" "}
                      — любые данные, предоставленные, отображаемые, загруженные
                      или обработанные Пользователем через Сервис, включая
                      содержимое экрана, системный звук и локальные документы.
                    </p>
                  </div>
                  <div className="rounded-lg bg-gray-800/30 p-4">
                    <p>
                      <strong className="text-white">Подписка</strong> — платный
                      или бесплатный доступ к функционалу Сервиса,
                      предоставляемый на определенный срок согласно выбранному
                      тарифному плану.
                    </p>
                  </div>
                </div>
              </Section>

              <Section id="services" title="3. Описание Услуг">
                <p>
                  NovioAI Assistant — это программный AI-ассистент,
                  предназначенный для повышения продуктивности пользователей. Он
                  работает в фоновом режиме и включает в себя:
                </p>
                <div className="my-6 grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="rounded-lg bg-gray-800/30 p-4">
                    <h4 className="mb-2 font-semibold text-white">
                      Интеллектуальный чат
                    </h4>
                    <p className="text-sm">
                      взаимодействие с ИИ на естественном языке
                    </p>
                  </div>
                  <div className="rounded-lg bg-gray-800/30 p-4">
                    <h4 className="mb-2 font-semibold text-white">
                      Распознавание экрана
                    </h4>
                    <p className="text-sm">
                      понимание отображаемой информации (код, текст, интерфейсы)
                    </p>
                  </div>
                  <div className="rounded-lg bg-gray-800/30 p-4">
                    <h4 className="mb-2 font-semibold text-white">
                      Анализ аудио
                    </h4>
                    <p className="text-sm">
                      обработка системного звука (включая транскрибацию встреч)
                    </p>
                  </div>
                  <div className="rounded-lg bg-gray-800/30 p-4">
                    <h4 className="mb-2 font-semibold text-white">
                      Работа с документами
                    </h4>
                    <p className="text-sm">
                      анализ и редактирование локальных файлов
                    </p>
                  </div>
                </div>
                <div className="rounded-lg border border-gray-700/50 bg-gray-800/20 p-6">
                  <h4 className="mb-4 font-semibold text-white">
                    Функциональность зависит от выбранного тарифа:
                  </h4>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="mt-1 text-green-400">•</span>
                      <span>
                        <strong className="text-white">Free</strong> — базовый
                        функционал, ограниченное количество запросов (например,
                        50 в месяц)
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 text-blue-400">•</span>
                      <span>
                        <strong className="text-white">Pro</strong> —
                        расширенный функционал, увеличенные лимиты
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 text-purple-400">•</span>
                      <span>
                        <strong className="text-white">Business</strong> —
                        корпоративный доступ с индивидуальными условиями
                      </span>
                    </li>
                  </ul>
                </div>
              </Section>

              <Section id="accounts" title="4. Учетные записи">
                <p>
                  Для использования отдельных функций Сервиса требуется
                  регистрация и создание Учетной записи. Пользователь обязуется:
                </p>
                <ul className="mt-4 list-disc space-y-2 pl-6">
                  <li>Предоставлять достоверную информацию при регистрации</li>
                  <li>Обеспечивать сохранность данных для входа</li>
                  <li>
                    Немедленно уведомлять Компанию о любом несанкционированном
                    доступе
                  </li>
                </ul>
                <div className="mt-4 rounded-lg border border-amber-800/50 bg-amber-900/20 p-4">
                  <p className="text-amber-300">
                    <strong>Возрастные ограничения:</strong> использование
                    Сервиса разрешено только лицам старше 18 лет либо достигшим
                    возраста, установленного законодательством своей страны для
                    заключения юридически обязывающих соглашений.
                  </p>
                </div>
              </Section>

              <Section
                id="usage-rules"
                title="5. Правила использования (Acceptable Use Policy)"
              >
                <p>Пользователь обязуется не:</p>
                <ul className="mt-4 list-disc space-y-2 pl-6">
                  <li>
                    Проводить реверс-инжиниринг, декомпиляцию или модификацию
                    Сервиса
                  </li>
                  <li>Пытаться взломать, нарушить или обойти защиту Сервиса</li>
                  <li>Использовать Сервис для незаконных целей</li>
                  <li>
                    Перепродавать или передавать лицензии третьим лицам без
                    письменного согласия Компании
                  </li>
                  <li>
                    Создавать чрезмерную нагрузку на серверную инфраструктуру
                  </li>
                  <li>
                    Использовать Сервис для обработки данных, утрата которых
                    может повлечь существенный ущерб
                  </li>
                </ul>
                <div className="mt-6 rounded-lg border border-red-800/50 bg-red-900/20 p-4">
                  <p className="text-red-300">
                    <strong>Важно:</strong> Сервис не предназначен для обработки
                    конфиденциальной, критически важной или государственной
                    информации. Компания не несет ответственности за последствия
                    такого использования.
                  </p>
                </div>
              </Section>

              <Section id="content" title="6. Контент и данные пользователя">
                <ul className="list-disc space-y-2 pl-6">
                  <li>
                    <strong className="text-white">
                      Права на ваш Контент.
                    </strong>{" "}
                    Все права на Контент пользователя (вводимый текст,
                    изображения, аудио) принадлежат исключительно Пользователю.
                    Мы не претендуем на право собственности на ваш Контент.
                  </li>
                  <li>
                    <strong className="text-white">
                      Лицензия для работы Сервиса.
                    </strong>{" "}
                    Для того чтобы Сервис мог выполнять свои функции, вы
                    предоставляете Компании ограниченную, неисключительную,
                    безвозмездную лицензию на использование, хостинг, передачу и
                    суб-лицензирование вашего Контента сторонним AI-провайдерам.
                    Эта лицензия действует исключительно с целью предоставления
                    вам функционала Сервиса и прекращается, как только вы
                    удаляете Контент или свою Учетную запись.
                  </li>
                  <li>
                    <strong className="text-white">
                      Передача данных сторонним AI-провайдерам.
                    </strong>{" "}
                    Для обработки ваших запросов Сервис передает часть вашего
                    Контента (например, текст запроса или анализируемые данные)
                    сторонним поставщикам AI-услуг. Мы не несем ответственности
                    за политики обработки данных этих провайдеров.
                  </li>
                  <li>
                    <strong className="text-white">Хранение данных.</strong> Мы
                    сохраняем историю ваших переписок с AI, чтобы вы могли
                    просматривать ее в любое время. Вы полностью контролируете
                    свою историю и можете удалить ее. Мы не используем ваши
                    переписки для обучения моделей.
                  </li>
                  <li>
                    Компания может собирать анонимные обезличенные данные
                    (телеметрию, техническую информацию, статистику
                    использования) для улучшения продукта.
                  </li>
                  <li>
                    Подробнее см. в{" "}
                    <Link
                      href="/privacy"
                      className="text-blue-400 hover:underline"
                    >
                      Политике конфиденциальности
                    </Link>
                    .
                  </li>
                </ul>
              </Section>

              <Section
                id="payment"
                title="7. Оплата, подписки и возврат средств"
              >
                <ul className="list-disc space-y-2 pl-6">
                  <li>
                    Подписка предоставляется на платной или бесплатной основе.
                    Платные подписки действуют по модели автопродления
                    (ежемесячной или ежегодной)
                  </li>
                  <li>
                    Списание средств осуществляется автоматически в начале
                    расчетного периода
                  </li>
                  <li>
                    Пользователь может отменить Подписку в любой момент, при
                    этом доступ сохраняется до окончания оплаченного периода
                  </li>
                  <li>
                    Возврат средств возможен только в случаях, прямо
                    предусмотренных законодательством или по усмотрению
                    Компании. В остальных случаях средства не возвращаются
                  </li>
                </ul>
              </Section>

              <Section id="ip" title="8. Интеллектуальная собственность">
                <ul className="list-disc space-y-2 pl-6">
                  <li>
                    Все исключительные права на программный код, интерфейс,
                    дизайн, базу данных, торговую марку и иные элементы Сервиса
                    принадлежат Компании
                  </li>
                  <li>
                    Пользователь получает ограниченную, неисключительную,
                    непередаваемую лицензию на использование Сервиса
                    исключительно в рамках настоящей Оферты
                  </li>
                  <li>
                    Контент пользователя остается в его собственности. Компания
                    не претендует на право владения таким контентом
                  </li>
                </ul>
              </Section>

              <Section
                id="termination"
                title="9. Прекращение действия договора"
              >
                <p>Компания оставляет за собой право:</p>
                <ul className="mt-4 list-disc space-y-2 pl-6">
                  <li>
                    Приостановить или прекратить доступ Пользователя к Сервису в
                    случае нарушения условий Оферты
                  </li>
                  <li>
                    Удалить Учетную запись Пользователя при длительной
                    неактивности (более 12 месяцев) с предварительным
                    уведомлением
                  </li>
                  <li>
                    Пользователь может прекратить использование Сервиса и
                    удалить свою учетную запись в любой момент через настройки
                    профиля
                  </li>
                </ul>
              </Section>

              <Section
                id="disclaimers"
                title="10. Отказ от гарантий и ограничение ответственности"
              >
                <ul className="list-disc space-y-2 pl-6">
                  <li>
                    Сервис предоставляется на условиях «как есть» (as is) и «как
                    доступно» (as available)
                  </li>
                  <li>
                    Компания не гарантирует бесперебойную или безошибочную
                    работу Сервиса
                  </li>
                  <li>
                    Компания не несет ответственности за любые прямые или
                    косвенные убытки, возникшие в результате использования или
                    невозможности использования Сервиса
                  </li>
                  <li>
                    В любом случае максимальная ответственность Компании
                    ограничивается суммой, уплаченной Пользователем за
                    использование Сервиса за последние три календарных месяца,
                    предшествующих возникновению претензии
                  </li>
                </ul>
              </Section>

              <Section id="disputes" title="11. Разрешение споров">
                <ul className="list-disc space-y-2 pl-6">
                  <li>
                    К настоящей Оферте применяется право Российской Федерации.
                  </li>
                  <li>
                    Все споры и разногласия подлежат разрешению путем
                    переговоров. Если урегулировать спор не удается, он подлежит
                    рассмотрению в компетентном суде по месту регистрации
                    Компании, если иное не предусмотрено императивными нормами
                    законодательства.
                  </li>
                </ul>
              </Section>

              <Section id="changes" title="12. Изменение условий">
                <ul className="list-disc space-y-2 pl-6">
                  <li>
                    Компания вправе в одностороннем порядке вносить изменения в
                    условия настоящей Оферты
                  </li>
                  <li>
                    Изменения вступают в силу с момента публикации новой
                    редакции на официальном сайте Сервиса
                  </li>
                  <li>
                    Пользователь обязан самостоятельно отслеживать актуальные
                    изменения. Продолжение использования Сервиса после
                    вступления изменений в силу означает согласие с новой
                    редакцией Оферты
                  </li>
                </ul>
              </Section>

              <Section id="requisites" title="13. Реквизиты Компании">
                <div className="space-y-3 rounded-lg bg-gray-800/30 p-6 text-sm">
                  <p>
                    <strong>Полное наименование:</strong> Индивидуальный
                    предприниматель Стрельников Станислав Дмитриевич
                  </p>
                  <p>
                    <strong>ИНН:</strong> 524926523957
                  </p>
                  <p>
                    <strong>Email для связи:</strong> terms@novioai.com
                  </p>
                </div>
              </Section>

              <Section id="contact" title="14. Контактная информация">
                <p>
                  По всем вопросам, связанным с настоящей Офертой, Пользователь
                  может связаться с Компанией по адресу электронной почты:
                </p>
                <div className="mt-4 rounded-lg bg-gray-800/30 p-6 text-center">
                  <div className="mb-2 flex items-center justify-center gap-2">
                    <Mail className="h-5 w-5 text-blue-400" />
                    <Link
                      href="mailto:terms@novioai.com"
                      className="font-medium text-blue-400 transition-colors hover:text-blue-300"
                    >
                      terms@novioai.com
                    </Link>
                  </div>
                  <p className="text-sm text-gray-400">
                    Мы ответим в течение 24 часов
                  </p>
                </div>
              </Section>

              <div className="mt-12 border-t border-gray-800 pt-8 text-center">
                <p className="mb-2 text-lg font-semibold text-white">
                  Благодарим за использование NovioAI!
                </p>
                <p className="text-gray-400">
                  Этот документ поможет вам лучше понять наши условия и защитить
                  ваши права.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-gray-800/50 bg-black py-16">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-4">
            <div className="md:col-span-2">
              <div className="mb-4 flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-white">
                  <Sparkles className="h-4 w-4 text-black" />
                </div>
                <span className="text-lg font-semibold text-white">
                  NovioAI
                </span>
              </div>
              <p className="max-w-md text-sm text-gray-400">
                AI-ассистент для повышения продуктивности. Работайте умнее, а не
                усерднее.
              </p>
            </div>

            <div>
              <h3 className="mb-4 text-sm font-medium text-white">Компания</h3>
              <ul className="space-y-3 text-sm text-gray-400">
                <li>
                  <Link
                    href="/terms"
                    className="transition-colors hover:text-white"
                  >
                    Публичная оферта
                  </Link>
                </li>
                <li>
                  <Link
                    href="/privacy"
                    className="transition-colors hover:text-white"
                  >
                    Политика конфиденциальности
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="mb-4 text-sm font-medium text-white">Контакты</h3>
              <ul className="space-y-3 text-sm text-gray-400">
                <li>
                  <a
                    href="mailto:support@novioai.com"
                    className="transition-colors hover:text-white"
                  >
                    support@novioai.com
                  </a>
                </li>
                <li>
                  <a
                    href="https://t.me/novioai"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="transition-colors hover:text-white"
                  >
                    Telegram
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-gray-800/50 pt-8 md:flex-row">
            <p className="text-center text-xs text-gray-500 md:text-left">
              © 2025 NovioAI. Все права защищены.
            </p>
            <div className="flex gap-6 text-xs">
              <Link
                href="/privacy"
                className="text-gray-500 transition-colors hover:text-gray-300"
              >
                Политика конфиденциальности
              </Link>
              <Link
                href="/terms"
                className="text-gray-500 transition-colors hover:text-gray-300"
              >
                Публичная оферта
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default OfferPage;
